import Handlebars from 'handlebars';
import { registerHandlebarsHelpers } from '../utils/handlebars-helpers';
import logger from '../utils/logger';

class PrintStore {

  constructor(rootStore) {
    this.rootStore = rootStore;
    registerHandlebarsHelpers(Handlebars);
  }

  print(file) {
    const newIframe = this.createIframe();
    newIframe.src = URL.createObjectURL(file);

    newIframe.onload = () => {
      setTimeout(() => {
        newIframe.contentWindow.focus();
        newIframe.contentWindow.print();
      }, 200);
    };

    this.removeOldIframe();
    document.body.appendChild(newIframe);
  }

  getBreakPage() {
    return '<div style="page-break-after: always;"></div>';
  }

  getTicketCopies(ticket, numberOfCopies = 1) {
    if (numberOfCopies === 1) {
      return ticket;
    }

    ticket += `${this.getBreakPage()}`;
    let ticketToPrint = '';

    for (let i = 1; i <= numberOfCopies; ++i) {
      ticketToPrint += ticket;
    }

    return ticketToPrint;
  }

  isAutoPrint() {
    const { settingsStore, batchStore, pacStore } = this.rootStore;
    const { configuration } = settingsStore;

    return configuration.autoPrint
      && batchStore.pacConnected
      && pacStore.printers.length;
  }

  printTemplate(ticket, numberOfCopies = 1, printer) {
    const { ticketStore, pacStore, notifierStore } = this.rootStore;
    const { timezone, template } = ticketStore;
    const termsTemplate = ticketStore.getTermsTemplate();
    const templates = termsTemplate
      ? `${template} ${this.getBreakPage()} ${termsTemplate}`
      : template;

    try {
      let tpl = Handlebars.compile(templates);
      let ticketToPrint = tpl({ ...ticket, timezone });

      if (this.isAutoPrint()) {
        return pacStore.print(ticketToPrint, numberOfCopies, printer)
          .catch(e => notifierStore.showError(`Can't print the ticket ${e.message}`));
      } else {
        ticketToPrint = this.getTicketCopies(ticketToPrint, numberOfCopies);
        return this.printHtml(ticketToPrint, null, false);
      }
    } catch (e) {
      notifierStore.showError(`Can't print the ticket ${e.message}`);
      return Promise.reject(e);
    }

  }

  getPrintWindowOpenTime() {
    if (!this.printWindowCloseTime || !this.printWindowOpenTime) {
      return 0;
    }

    if ((Date.now() - this.printWindowCloseTime) / 1000 > 5) {
      logger.log('print window was closed more than 5 sec ago');
      return 0;
    }

    const openTime = (this.printWindowCloseTime - this.printWindowOpenTime) / 1000;
    logger.log('print window has been opened for', openTime);

    return openTime;
  }

  printHtml(html = '', css = '', withTemplateWrapper = true) {
    const { pacStore, notifierStore, batchStore } = this.rootStore;
    html = withTemplateWrapper
      ? this.getTemplateWrapper(html, css)
      : html;

    if (pacStore.isWSOpen() && batchStore.pacConnected && batchStore.running.length) {
      notifierStore.showInfo('Printing in progress — no popup will appear. Please check your printer');
      return pacStore.print(html);
    }

    this.printWindowOpenTime = Date.now();

    return new Promise((resolve, reject) => {
      const newIframe = document.createElement('iframe');
      newIframe.src = 'about:blank';

      newIframe.style.display = 'none';

      newIframe.onload = () => {
        try {
          newIframe.contentWindow.document.body.innerHTML = html;

          setTimeout(() => {
            newIframe.contentWindow.focus();
            newIframe.contentWindow.print();

            // remove iframe after print
            newIframe.parentElement.removeChild(newIframe);
            this.printWindowCloseTime = Date.now();

            resolve();
          }, 200);
        } catch (err) {
          this.printWindowCloseTime = Date.now();
          reject(err);
        }
      };

      document.body.appendChild(newIframe);
      newIframe.onerror = (err) => reject(err);
    });

  }

  removeOldIframe() {
    const oldIframe = document.querySelector('pdf-frame');

    if (oldIframe) {
      document.body.removeChild(oldIframe);
    }
  }

  createIframe() {
    let newIframe = document.createElement('iframe');
    newIframe.setAttribute('name', 'pdf-frame');
    newIframe.style.position = 'absolute';
    newIframe.style.top = '-50000px';
    newIframe.style.left = '-50000px';
    newIframe.style.width = '2000px';
    newIframe.style.height = '2000px';

    return newIframe;
  }

  getTemplateWrapper(html = '', css = '') {
    return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <title>Print</title>
        <meta charset='utf-8'>
        <meta http-equiv='X-UA-Compatible' content='IE=edge'>
        <meta content='width=device-width, initial-scale=1' name='viewport'>
        <style>
            *, *:before, *:after {
                box-sizing: border-box;
            }
            body {
                width: 100%;
                margin: 0;
                padding: 0;
                font-size: 12px;
                line-height: 1.5;
                font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;
                color: rgb(33,33,33);
            }
            .wrapper {
                padding: 20px 30px;
            }
            .logo {
                max-width: 100%;
                max-height: 40px;
            }
            h2 {
                font-size: 16px;
            }
            h5 {
                font-size: 12px;
                font-weight: 300;
                padding: 15px 0;
                margin: 0;
            }
            p {
                padding: 5px 0;
                margin: 0;
            }
            .text-right {
                text-align: right;
            }
            .text-left {
                text-align: left;
            }
            .table, .rt-table {
                width: 100%;
                max-width: 100%;
                margin: 20px 0;
                border-collapse: collapse;
                border-spacing: 0;
                background-color: transparent;
            }
            .table > tbody > tr > td, .table > tfoot > tr > th, .table > tfoot > tr > td, .rt-td {
                padding: 3px 5px;
                line-height: 1.5;
                font-size: 12px;
                vertical-align: top;
                border-top: 1px solid rgb(231, 236, 241);
            }
            .table tr th, .rt-th {
                font-size: 12px;
                font-weight: 600;
                padding: 5px;
                vertical-align: bottom;
                border-bottom: 2px solid rgb(231, 236, 241);
            }
            .table > thead:first-child > tr:first-child > th, .table > thead:first-child > tr:first-child > td {
                border-top: 0;
            }
            .table-ticket-weights td, .table-ticket-weights th, .table-ticket-products td, .table-ticket-products th {
                text-align: right;
            }
            .table-ticket-weights td:first-child, .table-ticket-weights th:first-child,
             .table-ticket-products td:first-child, .table-ticket-products th:first-child,
              .table-ticket-products td:nth-child(2), .table-ticket-products th:nth-child(2) {
                text-align: left;
            }
            .table-ticket-products .row-ticket-products-total td {
                text-align: right;
            }
            .rt-table {
                flex: auto 1;
                display: flex;
                flex-direction: column;
                align-items: stretch;
                margin: 25px 0;
            }
            .rt-tr {
                flex: 1 0 auto;
                display: inline-flex;
            }
            .rt-table .rt-th, .rt-table .rt-td {
                flex: 1 0;
                white-space: nowrap;
            }
            .text-right {
                text-align: right;
            }
            .row {
                display: block;
                clear: both;
            }
            .row:before, .row:after {
                content: " ";
                display: table;
            }
            .col-sm-3, .col-sm-4, .col-sm-6 {
                float: left;
                position: relative;
                min-height: 1px;
            }
            .col-sm-3 {
                width: 25%;
            }
            .col-sm-4 {
                width: 33.33333%;
            }
            .col-sm-6 {
                width: 50%;
            }
            input {
                border: 0 none;
                padding: 0;
                outline: 0 none;
            }
            @page {
                size: A4;
                margin: 0;
            }
            ${css}
        </style>
    </head>
    <body>
        <div class="wrapper">
        ${html}
        </div>
    </body>
    </html>`;
  }

}

export default PrintStore;
